import { Monitor, Moon, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getThemePref, setThemePref, THEME_EVENT, type ThemePref } from '../theme.ts';

const OPTIONS: { value: ThemePref; icon: typeof Sun }[] = [
  { value: 'light', icon: Sun },
  { value: 'dark', icon: Moon },
  { value: 'auto', icon: Monitor },
];

/**
 * Three-way theme switch for the header: light, dark, or follow the device. `compact` (phones) is one button that
 * cycles through the three.
 */
export function ThemeSwitch({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const [pref, setPref] = useState<ThemePref>(getThemePref);
  useEffect(() => {
    const sync = () => setPref(getThemePref());
    window.addEventListener(THEME_EVENT, sync);
    return () => window.removeEventListener(THEME_EVENT, sync);
  }, []);
  if (compact) {
    const i = OPTIONS.findIndex((o) => o.value === pref);
    const next = OPTIONS[(i + 1) % OPTIONS.length]!.value;
    const Icon = OPTIONS[i]!.icon;
    return (
      <button
        type="button"
        className="icon-btn"
        aria-label={`${t('common.theme.label')}: ${t(`common.theme.${pref}`)}`}
        title={t(`common.theme.${pref}`)}
        data-testid="theme-cycle"
        onClick={() => {
          setThemePref(next);
          setPref(next);
        }}
      >
        <Icon size={16} aria-hidden="true" />
      </button>
    );
  }
  return (
    <div
      className="theme-switch"
      role="radiogroup"
      aria-label={t('common.theme.label')}
      data-testid="theme-switch"
    >
      {OPTIONS.map(({ value, icon: Icon }) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={pref === value}
          title={t(`common.theme.${value}`)}
          aria-label={t(`common.theme.${value}`)}
          data-theme-option={value}
          onClick={() => {
            setThemePref(value);
            setPref(value);
          }}
        >
          <Icon size={15} aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}
